import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import {
	clearAuthParams,
	clearStorage,
	getCodeVerifier,
	getSsoTargetLinkUri,
	getState,
	storeIdToken,
} from './authHelpers'

export function useAuthCallback() {
	const navigate = useNavigate()
	const sessionState = sessionStore.getState()
	const { showToast } = useToast()

	const { mutate: startSession, isPending } = useMutation({
		...trpc.client.auth.login.mutationOptions({
			onSuccess: (result) => {
				if (result.success) {
					sessionState.login(result.user)
					if (result.idToken) {
						storeIdToken(result.idToken)
					}
					const redirectPath = getSsoTargetLinkUri() || sessionState.redirectPath || '/'
					sessionStore.setState({ redirectPath: null })
					navigate(redirectPath)
				} else {
					sessionState.logout()
					navigateToLogin()
				}
				clearAuthParams()
			},
			onError: (error) => {
				showToast(error.message, 'error')
				sessionState.logout()
				clearStorage()
				navigateToLogin()
			},
		}),
	})

	const navigateToLogin = () => navigate('/auth/login')

	const handleAuthCallback = async (params: URLSearchParams) => {
		const code = params.get('code')
		const state = params.get('state')
		const errorParam = params.get('error')

		if (errorParam) {
			throw new Error(`Okta authentication error: ${errorParam}`)
		}

		const savedState = getState()
		const codeVerifier = getCodeVerifier()

		if (!code || state !== savedState || !codeVerifier) {
			throw new Error('Invalid authentication parameters')
		}

		startSession({
			code,
			codeVerifier,
			redirectUri: `${window.location.origin}/auth/callback`,
		})
	}

	return {
		handleAuthCallback,
		navigateToLogin,
		isPending,
	}
}
